import React from 'react';
import {
  Box, Typography, Paper, List, ListItem, ListItemAvatar, ListItemText,
  Avatar, Chip, Button, Divider, IconButton, Tooltip, Container
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  EventNote as EventNoteIcon,
  Notifications as NotificationsIcon,
  DoneAll as DoneAllIcon,
  ArrowBack as ArrowBackIcon,
  BeachAccess as BeachAccessIcon,
  Campaign as CampaignIcon,
  Assignment as AssignmentIcon,
  ClassOutlined as ClassIcon,
  Balance as BalanceIcon,
  HowToReg as HowToRegIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import EmptyState from '../components/EmptyState';
import { resolveNotificationRoute } from '../utils/notificationRoute';

const getNotifStyle = (type) => {
  switch (type) {
    case 'leave_approved':
      return { icon: <CheckCircleIcon />, color: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'Approved', labelColor: 'success' };
    case 'leave_rejected':
      return { icon: <CancelIcon />, color: '#ef4444', bg: 'rgba(239,68,68,0.12)', label: 'Rejected', labelColor: 'error' };
    case 'leave_request':
      return { icon: <EventNoteIcon />, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Leave Request', labelColor: 'warning' };
    case 'holiday_created':
      return { icon: <BeachAccessIcon />, color: '#06b6d4', bg: 'rgba(6,182,212,0.12)', label: 'Holiday', labelColor: 'info' };
    case 'announcement_created':
      return { icon: <CampaignIcon />, color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', label: 'Announcement', labelColor: 'secondary' };
    case 'exam_created':
      return { icon: <AssignmentIcon />, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'New Exam', labelColor: 'warning' };
    case 'class_assigned':
      return { icon: <ClassIcon />, color: '#2563eb', bg: 'rgba(37,99,235,0.12)', label: 'Assignment', labelColor: 'primary' };
    case 'leave_balance_updated':
      return { icon: <BalanceIcon />, color: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'Leave Balance', labelColor: 'success' };
    case 'weekly_plan_submitted':
      return { icon: <AssignmentIcon />, color: '#6366f1', bg: 'rgba(99,102,241,0.12)', label: 'Weekly Plan', labelColor: 'default' };
    case 'marks_submitted':
      return { icon: <AssignmentIcon />, color: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'Marks', labelColor: 'success' };
    case 'attendance_submitted':
      return { icon: <HowToRegIcon />, color: '#2563eb', bg: 'rgba(37,99,235,0.12)', label: 'Attendance', labelColor: 'primary' };
    case 'meeting_scheduled':
      return { icon: <GroupsIcon />, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Meeting', labelColor: 'warning' };
    case 'meeting_completed':
      return { icon: <GroupsIcon />, color: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'Completed', labelColor: 'success' };
    default:
      return { icon: <NotificationsIcon />, color: '#667eea', bg: 'rgba(102,126,234,0.12)', label: 'Notification', labelColor: 'default' };
  }
};

const CATEGORY = {
  attendance_submitted: 'Attendance',
  marks_submitted: 'Academics',
  exam_created: 'Academics',
  weekly_plan_submitted: 'Academics',
  class_assigned: 'Academics',
  meeting_scheduled: 'Meetings',
  meeting_completed: 'Meetings',
  announcement_created: 'Communication',
  holiday_created: 'Communication',
  leave_request: 'Leave',
  leave_approved: 'Leave',
  leave_rejected: 'Leave',
  leave_balance_updated: 'Leave',
  expense: 'Finance',
  low_stock_alert: 'Inventory',
  daycare_auto_checkout: 'Daycare',
  daily_update: 'Daycare',
  parent_query: 'Queries',
  query_completed: 'Queries',
  care_note_completed: 'Care Notes',
};
const CATEGORY_ORDER = ['Attendance', 'Academics', 'Meetings', 'Communication', 'Leave', 'Finance', 'Inventory', 'Daycare', 'Queries', 'Care Notes', 'Other'];

const groupByCategory = (notifications) => {
  const groups = {};
  notifications.forEach((n) => {
    const cat = CATEGORY[n.type] || 'Other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(n);
  });
  Object.values(groups).forEach((list) => list.sort((a, b) => (a.is_read === b.is_read ? 0 : a.is_read ? 1 : -1)));
  return CATEGORY_ORDER.filter((cat) => groups[cat]?.length).map((cat) => [cat, groups[cat]]);
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
};

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();

  const handleBack = () => navigate(-1);

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) await markRead(notif.id);
    const href = resolveNotificationRoute(notif, user?.role);
    if (href) navigate(href);
  };

  const groups = groupByCategory(notifications);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box
        display="flex"
        sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}
        gap={2}
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2} flex={1}>
          <IconButton onClick={handleBack} sx={{ bgcolor: 'rgba(102,126,234,0.1)', '&:hover': { bgcolor: 'rgba(102,126,234,0.2)' } }}>
            <ArrowBackIcon sx={{ color: '#667eea' }} />
          </IconButton>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            </Typography>
          </Box>
        </Box>
        {unreadCount > 0 && (
          <Tooltip title="Mark all as read">
            <Button
              variant="outlined"
              startIcon={<DoneAllIcon />}
              onClick={markAllRead}
              sx={{ borderColor: '#667eea', color: '#667eea', fontWeight: 600, textTransform: 'none', alignSelf: { xs: 'flex-end', sm: 'center' } }}
            >
              Mark all read
            </Button>
          </Tooltip>
        )}
      </Box>

      {notifications.length === 0 ? (
        <EmptyState
          icon={NotificationsIcon}
          title="No notifications yet"
          description="Notifications will appear here when there's activity."
        />
      ) : (
        groups.map(([category, items]) => (
          <Paper key={category} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'rgba(0,0,0,0.02)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                {category}
              </Typography>
            </Box>
            <List disablePadding>
              {items.map((notif, idx) => {
                const style = getNotifStyle(notif.type);
                const unread = !notif.is_read;
                return (
                  <React.Fragment key={notif.id}>
                    <ListItem
                      button
                      onClick={() => handleNotifClick(notif)}
                      sx={{
                        px: 2.5, py: 2,
                        bgcolor: unread ? 'rgba(102,126,234,0.03)' : 'transparent',
                        opacity: unread ? 1 : 0.75,
                        '&:hover': { bgcolor: unread ? 'rgba(102,126,234,0.08)' : 'rgba(0,0,0,0.03)', opacity: 1 },
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListItemAvatar sx={{ mt: 0.5 }}>
                        <Avatar sx={{ bgcolor: unread ? style.bg : 'rgba(0,0,0,0.06)', color: style.color, width: 44, height: 44 }}>
                          {style.icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body1" fontWeight={unread ? 700 : 500} sx={{ fontSize: '0.9rem' }}>
                              {notif.title}
                            </Typography>
                            <Chip label={style.label} size="small" color={style.labelColor} variant={unread ? 'filled' : 'outlined'}
                              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }} />
                            {unread && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#667eea', ml: 'auto', flexShrink: 0 }} />}
                          </Box>
                        }
                        secondary={
                          <Box mt={0.5}>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                              {notif.message}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                              {timeAgo(notif.created_at)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < items.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        ))
      )}
    </Container>
  );
};

export default Notifications;
